
import { formatCurrency } from '@pinflag/pinflag-ui-kit'

import { resolveMapboxToken } from '../utils/encrypt'
import { loadPinmap } from './abTest'
import companyApiInstance from './companyApiInstance'
import { iframeLoadedShopify, iframeLoadedWoocommerceVtex } from './iframeLoadedCmsHelper'
import { removeDisabledKlap } from './klapSdk'
import { getUserInfoLocalStorage } from './localStorage'
import { getPinflagIdentity } from './utils'

const REQUEST_TIMEOUT = 2000

const processCompanyData = (data, pinflagApiKey) => {
  let labelPricePickup = data.storePickupPriceLabel
  if (/^\d+$/.test(String(data.storePickupPriceLabel))) {
    labelPricePickup = formatCurrency(Number(data.storePickupPriceLabel), data.country)
  } else if (!isNaN(parseInt(data.storePickupPriceLabel))) {
    labelPricePickup = '$' + data.storePickupPriceLabel
  }

  return {
    logoUrl: data.logoUrl,
    primaryColor: data.primaryColor || '#394860',
    secondaryColor: data.secondaryColor || '#00B7F9',
    companyName: data.company?.nombreempresa || data.companyName,
    userId: data.company?.userId || data.userId,
    pinflagApiKey,
    ccDiscountPercentageLabel: data.ccDiscountPercentageLabel,
    storePickupPriceLabel: labelPricePickup,
    storePickupTypeLabel: data.storePickupTypeLabel,
    checkout: data.checkout,
    useBillingData: data.useBillingData,
    marketingCheckboxDefault: data.marketingCheckboxDefault,
    useTwoClick: data.useTwoClick ?? 0,
    ownPayments: removeDisabledKlap(data.payment || data.ownPayments),
    useSsn: data.useSsn,
    useDiscountCode: data.useDiscountCode,
    useGiftCard: data.useGiftCard,
    personalization: data.personalization,
    paymentPersonalization: data.paymentPersonalization,
    storePickupDiscountBanner: data.storePickupDiscountBanner,
    mapboxToken: resolveMapboxToken(data.mapboxToken),
    usePixelEvents: data.usePixelEvents,
    useOneClick: data.useOneClick,
    useGift: data.useGift,
    useGiftMessage: data.useGiftMessage ?? true,
    useOrderNotes: data.useOrderNotes,
    selectedCheckout: data.selectedCheckout,
    customBillingNames: data.cmsBillingFieldsCustomNames?.fields,
    billingDocName: data.cmsBillingFieldsCustomNames?.docName,
    activeBillingFields: data.cmsActiveBillingFields,
    thankyouPageText: data.thankyouPageText,
    contactEmail: data.company?.contactEmail || data.contactEmail,
    shippingSort: data.shippingSort ?? null,
    country: data.country ?? null
  }
}

const setCompanyInfoData = (
  companyObject,
  setCompanyInfo,
  setIsSaving
) => {
  const pinflagApiKey = companyObject.pinflagApiKey

  iframeLoadedShopify(loadPinmap(companyObject.checkout), {
    companyName: companyObject.companyName,
    cms: 'shopify',
    pinflagApiKey
  })
  iframeLoadedWoocommerceVtex(loadPinmap(companyObject.checkout))

  setCompanyInfo((prevState) => ({
    ...prevState,
    ...companyObject,
    mapboxToken:
      companyObject.mapboxToken &&
      !['null', 'undefined'].includes(companyObject.mapboxToken)
        ? companyObject.mapboxToken
        : process.env.REACT_APP_MAPBOX_TOKEN
  }))
  setIsSaving(companyObject.useOneClick > 0)
}

const requestCompanyInfoFromParent = (pinflagApiKey, onResponse) => {
  if (window.self === window.top) {
    onResponse(null)
    return
  }

  let resolved = false
  const timeoutId = setTimeout(() => {
    if (!resolved) {
      resolved = true
      window.removeEventListener('message', messageHandler)
      onResponse(null)
    }
  }, REQUEST_TIMEOUT)

  const messageHandler = (event) => {
    if (
      event.source === window.parent &&
      event.data?.type === 'response-company-info' &&
      event.data?.pinflagApiKey === pinflagApiKey &&
      !resolved
    ) {
      resolved = true
      clearTimeout(timeoutId)
      window.removeEventListener('message', messageHandler)
      onResponse(event.data.message)
    }
  }

  window.addEventListener('message', messageHandler)

  window.parent.postMessage(
    {
      type: 'request-company-info',
      origin: 'pinflag-shopify-pinmap-pro',
      pinflagApiKey,
      message: true
    },
    '*'
  )
}

let companyInfoRequest = null

const fetchCompanyInfoFromAPI = (pinflagApiKey) => {
  if (!companyInfoRequest) {
    companyInfoRequest = companyApiInstance(pinflagApiKey)
      .get('/company/personalization', {
        cache: {
          maxAge: 5 * 60 * 1000,
          exclude: { query: false }
        }
      })
      .then((res) => res.data)
      .catch((err) => {
        companyInfoRequest = null
        console.error(err)
        throw err
      })
  }
  return companyInfoRequest
}

export const getCompanyInfoHelper = (
  setUserInfo,
  setCompanyInfo,
  setIsSaving,
  setAbandonedCartData,
  isAbandonedCart
) => {
  const preloadUserInfo = () => {
    const userInfoLocalStorage = getUserInfoLocalStorage()
    setAbandonedCartData(previousState => ({
      ...previousState,
      email: userInfoLocalStorage.email,
      names: userInfoLocalStorage.names,
      phone: userInfoLocalStorage.phone,
      rut: userInfoLocalStorage.rut
    }))
    setUserInfo(prevState => ({
      ...prevState,
      ...userInfoLocalStorage
    }))
  }

  const pinflagApiKey = getPinflagIdentity()

  if (!pinflagApiKey) {
    return
  }

  requestCompanyInfoFromParent(pinflagApiKey, (parentCompanyInfo) => {
    if (parentCompanyInfo) {
      const companyObject = processCompanyData(
        parentCompanyInfo,
        pinflagApiKey
      )
      if (companyObject.useOneClick === 0 && !isAbandonedCart) preloadUserInfo()
      setCompanyInfoData(
        companyObject,
        setCompanyInfo,
        setIsSaving
      )
      return
    }

    fetchCompanyInfoFromAPI(pinflagApiKey)
      .then((apiData) => {
        const companyObject = processCompanyData(apiData, pinflagApiKey)
        if (companyObject.useOneClick === 0 && !isAbandonedCart) preloadUserInfo()
        setCompanyInfoData(
          companyObject,
          setCompanyInfo,
          setIsSaving
        )
      })
      .catch((err) => {
        iframeLoadedShopify(false)
        iframeLoadedWoocommerceVtex(false)
        setCompanyInfo((prevState) => ({
          ...prevState,
          pinflagApiKey
        }))
        console.error(err)
      })
  })
}
